var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { login } from "@services/AuthService";
import "@styles/css/login/login.css";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BeoButton } from "@components/common/action";
import { BeoInputText, BeoPassword } from "@components/common/input";
import { BeoConfirmDialouge } from "@components/common/view";
import { useAppInitialization } from "@hooks/useAppInitialization";
import BeoCaptcha from "@pages/common/components/BeoCaptchaValidator/BeoCaptcha";
import { getUserRole } from "@utils/auth";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
var CAPTCHA_INVALID = "Please enter a valid CAPTCHA.";
var CAPTCHA_REQUIRED = "CAPTCHA is required.";
var CAPTCHA_ATTEMPT_THRESHOLD = 3;
var ATTEMPTS_KEY = "loginAttempts";
/** Loads failed login attempts from local storage. */
var getFailedAttempts = function () {
    var attempts = localStorage.getItem(ATTEMPTS_KEY);
    return attempts ? parseInt(attempts, 10) || 0 : 0;
};
/** Stores failed login attempts in local storage. */
var setFailedAttempts = function (count) {
    localStorage.setItem(ATTEMPTS_KEY, count.toString());
};
/** CAPTCHA is required for the admin user or after repeated failures. */
var shouldShowCaptcha = function (username) {
    var isAdmin = (username || "").trim().toLowerCase() === "admin";
    return isAdmin || getFailedAttempts() >= CAPTCHA_ATTEMPT_THRESHOLD;
};
/**
 * Login component provides a user authentication interface.
 * Handles login, form validation (react-hook-form), CAPTCHA gating,
 * and concurrent-session handling.
 *
 * @author niyas.shajahan
 * @since 2025-06-19
 */
var Login = function (_a) {
    var setIsAuthenticated = _a.setIsAuthenticated;
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    var _c = useState(null), errorMessage = _c[0], setErrorMessage = _c[1];
    var _d = useState(false), captchaValid = _d[0], setCaptchaValid = _d[1];
    var _e = useState(false), captchaEntered = _e[0], setCaptchaEntered = _e[1];
    var _f = useState(false), showCaptcha = _f[0], setShowCaptcha = _f[1];
    var _g = useState(false), isFailed = _g[0], setIsFailed = _g[1];
    var _h = useForm({
        defaultValues: { password: "" },
    }), register = _h.register, control = _h.control, handleSubmit = _h.handleSubmit, errors = _h.formState.errors;
    var navigate = useNavigate();
    var initialMethod = useAppInitialization().initialMethod;
    // Stable callbacks so BeoCaptcha's refreshCaptcha isn't recreated every render
    var handleVerify = useCallback(function (isValid) {
        setCaptchaValid(isValid);
        if (isValid) {
            // Only clear CAPTCHA-related errors, and only when it's actually valid
            setErrorMessage(function (prev) {
                return prev === CAPTCHA_INVALID || prev === CAPTCHA_REQUIRED ? null : prev;
            });
        }
    }, []);
    var handleInputChange = useCallback(function (value) {
        setCaptchaEntered(value.trim().length > 0);
        if (value.length > 0) {
            setErrorMessage(function (prev) { return (prev === CAPTCHA_REQUIRED ? null : prev); });
        }
    }, []);
    var handleResetFail = useCallback(function () { return setIsFailed(false); }, []);
    /**
     * Handles the form submission for user login.
     */
    var onSubmit = function (data) { return __awaiter(void 0, void 0, void 0, function () {
        var needsCaptcha, loginResponse, role, route, nowNeedsCaptcha, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    needsCaptcha = shouldShowCaptcha(data.username);
                    setShowCaptcha(needsCaptcha);
                    if (needsCaptcha) {
                        if (!captchaEntered) {
                            setErrorMessage(CAPTCHA_REQUIRED);
                            return [2 /*return*/];
                        }
                        if (!captchaValid) {
                            setErrorMessage(CAPTCHA_INVALID);
                            setIsFailed(true);
                            return [2 /*return*/];
                        }
                    }
                    setLoading(true);
                    setErrorMessage(null);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 6, 7, 8]);
                    return [4 /*yield*/, login(data)];
                case 2:
                    loginResponse = _b.sent();
                    if (!loginResponse.accessToken) return [3 /*break*/, 4];
                    setFailedAttempts(0); // reset on any successful login
                    role = getUserRole();
                    if (!role) {
                        setErrorMessage("Unable to determine user role. Please try again.");
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, initialMethod()];
                case 3:
                    route = _b.sent();
                    if (route !== "/unauthorized") {
                        setIsAuthenticated(true);
                        sessionStorage.setItem("isAuthenticated", "true");
                    }
                    navigate(route);
                    return [3 /*break*/, 5];
                case 4:
                    if ((_a = loginResponse.error) === null || _a === void 0 ? void 0 : _a.includes("session already active")) {
                        confirmSessionLogout(data);
                    }
                    else if (loginResponse.error) {
                        setFailedAttempts(getFailedAttempts() + 1);
                        setErrorMessage(loginResponse.error);
                        nowNeedsCaptcha = shouldShowCaptcha(data.username);
                        setShowCaptcha(nowNeedsCaptcha);
                        if (nowNeedsCaptcha)
                            setIsFailed(true);
                    }
                    _b.label = 5;
                case 5: return [3 /*break*/, 8];
                case 6:
                    error_1 = _b.sent();
                    console.error("Login error:", error_1);
                    setErrorMessage("An unexpected error occurred. Please try again.");
                    if (needsCaptcha)
                        setIsFailed(true);
                    return [3 /*break*/, 8];
                case 7:
                    setLoading(false);
                    return [7 /*endfinally*/];
                case 8: return [2 /*return*/];
            }
        });
    }); };
    /**
     * Confirms logging out other sessions, then retries login.
     */
    var confirmSessionLogout = function (authRequest) {
        confirmDialog({
            message: "Do you want to log out from all devices and log in again?",
            header: "Session Already Active",
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "accept",
            accept: function () { return __awaiter(void 0, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    authRequest.forceLogin = true;
                    onSubmit(authRequest);
                    return [2 /*return*/];
                });
            }); },
            reject: function () {
                setErrorMessage("Login canceled. Please log out from other devices first.");
            },
        });
    };
    var onInvalidSubmit = function () {
        setErrorMessage(null);
    };
    // Keep RHF's onBlur AND run our CAPTCHA check
    var usernameField = register("username", {
        required: "Username is required",
    });
    return (_jsxs("div", { className: "min-h-screen flex items-center justify-center login-page", children: [_jsxs("section", { className: "w-full max-w-md px-4 py-8", children: [_jsx("div", { className: "beo-logo mx-auto mb-8" }), _jsx("main", { className: "backdrop-blur-xl bg-white/75 rounded-xl shadow-2xl p-10 w-full", children: _jsxs("div", { className: "space-y-6", children: [_jsxs("form", { onSubmit: handleSubmit(onSubmit, onInvalidSubmit), className: "space-y-4", children: [_jsx("div", { className: "eakz-logo" }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { htmlFor: "username", className: "block mb-1 text-sm text-gray-900", children: "Username" }), _jsx(BeoInputText, __assign({ id: "username", placeholder: "Enter your username" }, usernameField, { onBlur: function (e) {
                                                        usernameField.onBlur(e);
                                                        setShowCaptcha(shouldShowCaptcha(e.target.value));
                                                    }, className: "w-full rounded-md border bg-gray-50 ".concat(errors.username ? "border-red-500" : "border-gray-300") })), errors.username && (_jsx("div", { className: "text-red-500 text-[11px] mt-1", children: errors.username.message }))] }), _jsx(Controller, { name: "password", control: control, rules: { required: "Password is required" }, render: function (_a) {
                                                var field = _a.field, fieldState = _a.fieldState;
                                                return (_jsxs("div", { className: "mb-6", children: [_jsx("label", { htmlFor: "password", className: "block mb-1 text-sm text-gray-900", children: "Password" }), _jsx(BeoPassword, __assign({ id: "password", placeholder: "Enter your password" }, field, { invalid: !!fieldState.error, className: "w-full", feedback: false, toggleMask: true })), fieldState.error && (_jsx("div", { className: "text-red-500 text-[11px] mt-1", children: fieldState.error.message }))] }));
                                            } }), showCaptcha && (_jsx(BeoCaptcha, { onVerify: handleVerify, onInputChange: handleInputChange, isFailed: isFailed, onResetFail: handleResetFail })), errorMessage && (_jsx("div", { className: "text-red-500 text-xs mb-3 text-center bg-red-900/10 p-2 rounded", children: errorMessage })), _jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsxs("div", { className: "flex items-center", children: [_jsx("input", { className: "h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded", type: "checkbox", id: "rememberMe" }), _jsx("label", { htmlFor: "rememberMe", className: "ml-2 block text-sm text-gray-700", children: "Remember me" })] }), _jsx("div", { className: "text-sm", children: _jsx("a", { href: "/forgot-password", className: "font-medium text-yellow-600 hover:text-yellow-500", children: "Forgot Password?" }) })] }), _jsx(BeoButton, { id: "login-btn", type: "submit", icon: "pi pi-sign-in", className: "w-full py-3 px-4 flex items-center justify-center text-lg font-bold rounded-md text-gray-900 bg-gradient-to-r from-yellow-300 to-orange-400 hover:from-yellow-400 hover:to-orange-500 transition-all duration-300 transform hover:scale-[1.02] shadow-lg", loading: loading, label: "Login", iconPos: "left", children: _jsx("span", { className: "ml-2", children: "\uD83D\uDD12" }) })] }), _jsxs("div", { className: "text-center mt-4 text-sm", children: [_jsx("span", { className: "text-gray-700", children: "New to eAKZ? " }), _jsx("a", { href: "/register", className: "font-medium text-yellow-600 hover:text-yellow-500", children: "Request an account" })] })] }) })] }), _jsx(BeoConfirmDialouge, { id: "confirm-dialog" })] }));
};
export default Login;
